import React, { useState, useEffect, useCallback } from 'react';
import { Calendar, dateFnsLocalizer } from 'react-big-calendar';
import format from 'date-fns/format';
import parse from 'date-fns/parse';
import startOfWeek from 'date-fns/startOfWeek';
import getDay from 'date-fns/getDay';
import ruLocale from 'date-fns/locale/ru';
import 'react-big-calendar/lib/css/react-big-calendar.css';
import style from './eventcalendar.module.scss';
import Button from '../Button/Button';
import { fetchBranches, fetchPrograms } from '../../services/api';

const localizer = dateFnsLocalizer({
  format,
  parse,
  startOfWeek,
  getDay,
  locales: { ru: ruLocale }
});

const EventsCalendar = ({ apiEvents = [] }) => {
  const [trainingEvents, setTrainingEvents] = useState([]);
  const [allEvents, setAllEvents] = useState([]);
  const [filteredEvents, setFilteredEvents] = useState([]);
  const [currentDate, setCurrentDate] = useState(new Date());
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [branches, setBranches] = useState([]);
  const [selectedBranch, setSelectedBranch] = useState(null);
  const [selectedEventType, setSelectedEventType] = useState('all');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [programs, setPrograms] = useState([]);

  // Event type options
  const eventTypes = [
    { value: 'all', label: 'Все события' },
    { value: 'training', label: 'Тренировки' },
    { value: 'event', label: 'События' },
    { value: 'other', label: 'Другое' }
  ];

  // Fetch initial data
  useEffect(() => {
    const loadData = async () => {
      try {
        setLoading(true);
        const [branchesResponse, programsResponse] = await Promise.all([
          fetchBranches({ populate: '*' }),
          fetchPrograms({ populate: '*' })
        ]);
        setBranches(branchesResponse.data || []);
        setPrograms(programsResponse.data || []);
      } catch (err) {
        console.error('Error loading data:', err);
        setError('Не удалось загрузить данные');
      } finally {
        setLoading(false);
      }
    };
    loadData();
  }, []);

  // Generate training events for the current view
  const generateTrainingEvents = useCallback((date) => {
    if (!programs.length) return [];

    const month = date.getMonth();
    const year = date.getFullYear();
    const daysInMonth = new Date(year, month + 1, 0).getDate();

    return programs.flatMap(program => {
      const programData = program.attributes;
      
      // Safely handle Schedule data
      const schedule = programData?.Schedule;
      if (!schedule || !Array.isArray(schedule)) {
        return [];
      }

      return schedule.flatMap(session => {
        // Validate session data
        if (!session?.WeekDay || !session?.StartTime || !session?.EndTime) {
          return [];
        }

        const dayIndex = ['ВС', 'ПН', 'ВТ', 'СР', 'ЧТ', 'ПТ', 'СБ'].indexOf(session.WeekDay);
        if (dayIndex === -1) return [];

        // Find all dates in this month for the weekday
        const dates = [];
        for (let day = 1; day <= daysInMonth; day++) {
          const date = new Date(year, month, day);
          if (date.getDay() === dayIndex) {
            dates.push(date);
          }
        }

        return dates.map(date => {
          const startDateTime = new Date(date);
          const [startHours, startMinutes] = session.StartTime.split(':').map(Number);
          startDateTime.setHours(startHours, startMinutes);
          
          const endDateTime = new Date(date);
          const [endHours, endMinutes] = session.EndTime.split(':').map(Number);
          endDateTime.setHours(endHours, endMinutes);
          
          return {
            id: `${program.id}-${session.id}-${date.getDate()}`,
            title: programData?.Title || 'Без названия',
            type: 'Тренировка',
            eventType: 'training',
            sport: programData?.sport?.data?.attributes?.Title || 'Спорт',
            coach: programData?.coaches?.data?.map(c => 
              `${c.attributes?.LastName || ''} ${c.attributes?.FirstName || ''} ${c.attributes?.PatronymicName || ''}`
            ).join(', ') || 'Тренер не указан',
            start: startDateTime,
            end: endDateTime,
            facility: programData?.branches?.data?.[0]?.attributes?.Title || 'Место не указано',
            description: programData?.Description
          };
        });
      });
    });
  }, [programs]);

  // Combine all events when data changes
  useEffect(() => {
    if (programs.length) {
      try {
        const trainingEvents = generateTrainingEvents(currentDate);
        setTrainingEvents(trainingEvents);
        
        // Combine training events with API events
        const combined = [...trainingEvents, ...apiEvents];
        setAllEvents(combined);
        applyFilters(combined, selectedBranch, selectedEventType);
      } catch (err) {
        console.error('Error generating events:', err);
        setError('Ошибка при формировании расписания');
      }
    }
  }, [currentDate, programs, generateTrainingEvents, apiEvents, selectedBranch, selectedEventType]);

  // Apply filters
  const applyFilters = (events, branchFilter, eventTypeFilter) => {
    let filtered = [...events];
    
    // Apply branch filter
    if (branchFilter) {
      filtered = filtered.filter(event => 
        event.facility === branchFilter.attributes?.Title
      );
    }
    
    // Apply event type filter
    if (eventTypeFilter && eventTypeFilter !== 'all') {
      if (eventTypeFilter === 'training') {
        filtered = filtered.filter(event => event.eventType === 'training');
      } else {
        filtered = filtered.filter(event => 
          event.type === eventTypeFilter || 
          (eventTypeFilter === 'event' && event.type === 'Событие') ||
          (eventTypeFilter === 'other' && event.type === 'Другое')
        );
      }
    }
    
    setFilteredEvents(filtered);
  };

  const handleNavigate = (newDate) => {
    setCurrentDate(newDate);
  };

  const handleEventSelect = (event) => {
    setSelectedEvent(event);
  };

  const closeModal = () => {
    setSelectedEvent(null);
  };

  const eventStyleGetter = (event) => {
    let backgroundColor = '#1d3e8c'; // Default blue for trainings
    
    if (event.eventType !== 'training') {
      backgroundColor = event.type === 'Событие' ? '#4CAF50' : '#9C27B0';
    }

    return {
      style: {
        backgroundColor,
        borderRadius: '4px',
        border: 'none',
        color: 'white',
        padding: '2px 5px',
        opacity: 0.8,
        fontSize: '0.9em'
      }
    };
  };

  if (loading) {
    return <div className={style.loadingContainer}>Загрузка событий...</div>;
  }

  if (error) {
    return (
      <div className={style.errorContainer}>
        <p>{error}</p>
        <Button 
          text="Попробовать снова"
          onClick={() => window.location.reload()}
        />
      </div>
    );
  }

  return (
    <div className={style.calendarWrapper}>
      <div className={style.filters}>
        <div className={style.branchFilters}>
          <h4>Муниципальные образования:</h4>
          <div className={style.branchButtons}>
            {branches.map(branch => (
              <Button
                key={branch.id}
                text={branch.attributes?.Title || 'Без названия'}
                onClick={() => {
                  setSelectedBranch(branch);
                  applyFilters(allEvents, branch, selectedEventType);
                }}
                background={selectedBranch?.id === branch.id ? '#1d3e8c' : '#f0f0f0'}
                color={selectedBranch?.id === branch.id ? '#fff' : '#333'}
                padding="0.5vw 1vw"
              />
            ))}
            <Button
              text="Все МО"
              onClick={() => {
                setSelectedBranch(null);
                applyFilters(allEvents, null, selectedEventType);
              }}
              background={!selectedBranch ? '#1d3e8c' : '#f0f0f0'}
              color={!selectedBranch ? '#fff' : '#333'}
              padding="0.5vw 1vw"
            />
          </div>
        </div>

        <div className={style.eventTypeFilters}>
          <h4>Тип события:</h4>
          <div className={style.eventTypeButtons}>
            {eventTypes.map(type => (
              <Button
                key={type.value}
                text={type.label}
                onClick={() => {
                  setSelectedEventType(type.value);
                  applyFilters(allEvents, selectedBranch, type.value);
                }}
                background={selectedEventType === type.value ? '#1d3e8c' : '#f0f0f0'}
                color={selectedEventType === type.value ? '#fff' : '#333'}
                padding="0.5vw 1vw"
              />
            ))}
          </div>
        </div>
      </div>

      <div className={style.calendarContainer}>
        {filteredEvents.length > 0 ? (
          <Calendar
            localizer={localizer}
            events={filteredEvents}
            startAccessor="start"
            endAccessor="end"
            style={{ height: '600px' }}
            messages={{
              today: 'Сегодня',
              previous: 'Назад',
              next: 'Вперед',
              month: 'Месяц',
              week: 'Неделя',
              day: 'День',
              agenda: 'Повестка',
              date: 'Дата',
              time: 'Время',
              event: 'Событие',
            }}
            defaultView="month"
            culture="ru"
            eventPropGetter={eventStyleGetter}
            onSelectEvent={handleEventSelect}
            onNavigate={handleNavigate}
            date={currentDate}
            views={['month', 'week', 'day']}
          />
        ) : (
          <div className={style.noEvents}>Нет событий для отображения</div>
        )}
      </div>

      {selectedEvent && (
        <div className={style.modalOverlay} onClick={closeModal}>
          <div className={style.modalContent} onClick={e => e.stopPropagation()}>
            <button className={style.closeButton} onClick={closeModal}>
              ×
            </button>
            <div className={style.eventModal}>
              <h2>{selectedEvent.title}</h2>
              <div className={style.eventInfo}>
                <p className={style.eventType}>Тип: {selectedEvent.type}</p>
                {selectedEvent.sport && (
                  <p className={style.eventSport}>Спорт: {selectedEvent.sport}</p>
                )}
                {selectedEvent.coach && (
                  <p className={style.eventCoach}>Тренер: {selectedEvent.coach}</p>
                )}
              </div>
              <p className={style.eventTime}>
                Время: {selectedEvent.start.toLocaleString('ru-RU', {
                  weekday: 'short',
                  day: 'numeric',
                  month: 'long',
                  hour: '2-digit',
                  minute: '2-digit'
                })} -{' '}
                {selectedEvent.end.toLocaleTimeString('ru-RU', {
                  hour: '2-digit',
                  minute: '2-digit'
                })}
              </p>
              {selectedEvent.facility && (
                <p className={style.eventLocation}>Место: {selectedEvent.facility}</p>
              )}
              {selectedEvent.description && (
                <div className={style.eventDescription}>
                  <p>Описание:</p>
                  <div dangerouslySetInnerHTML={{ __html: selectedEvent.description }} />
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default EventsCalendar;